import { createApi } from '@reduxjs/toolkit/query/react';
import { HaveFinish } from 'utils/finishes';
import { baseQuery } from '../../baseQuery';

/**
 * Coarse activity bucket, never a timestamp.
 *
 * The backend derives this from the holder's last login and deliberately never
 * emits the underlying date — a bucket is not reversible into a login log. Note
 * `dormant` is the API value; the UI renders it as "inactive". Null when the
 * holder has no recorded login, in which case nothing is rendered.
 */
export type HolderActivity = 'today' | 'this_week' | 'this_month' | 'dormant';

/**
 * One offerable COPY of a printing. The picker groups these by userId into one
 * row per PERSON — a holder offering the same printing in NM and LP is two
 * entries here but a single picker row.
 *
 * Field names mirror the wire exactly. Three of them differ from what the
 * original spec assumed, and the wire wins: `completedTradeCount` (not
 * tradeCount), `lang` (not language), `quotedPricePoints` (not pricePoints).
 */
export interface CardHolder {
  // --- Offer-builder seed fields. These map onto OfferLineItem. ---
  userId: number;
  username: string;
  /** The specific have row. This is what a pick resolves to. */
  userMTGCardHaveId: number;
  ownedQuantity: number;
  /**
   * Price of THIS copy, in points. Never null: the backend omits rows that
   * price to 0 entirely, because the builder rejects null-price lines.
   */
  quotedPricePoints: number;
  /** Prisma enum, e.g. "NEAR_MINT". */
  condition: string;
  foil: boolean;
  /** Track D; derive from foil when absent */
  finish?: HaveFinish | null;
  /** Lowercase Scryfall code, e.g. "en". */
  lang: string;
  card: {
    id: number;
    oracleId: string | null;
    scryfallId: string | null;
    name: string;
    /** Nullable on the wire, but non-null on OfferCard — coerce when mapping. */
    setCode: string | null;
    setName: string | null;
    collectorNumber: string | null;
    lang: string;
    imageUrl: string | null;
  };

  // --- Display-only: trade record, badges, activity. ---
  completedTradeCount: number;
  isElite: boolean;
  isNew: boolean;
  activity: HolderActivity | null;

  /**
   * The holder's OWN points balance (User.points) — their spending power, not
   * this card's price, which is `quotedPricePoints`. Backs the "Most points"
   * sort and the labelled balance on each picker row. The wire name is
   * deliberately not `points`: beside `quotedPricePoints` that would invite
   * exactly the mix-up the labelled UI exists to prevent.
   *
   * Optional purely as a deploy-order guard — the backend ships it on every row
   * (verified live), but staging and production deploy independently. Render
   * nothing, never 0, when it is absent: a holder shown with a 0 balance reads
   * as "cannot afford anything", which is false. Same guard `holderCount?` uses
   * in types/interfaces/mtgCard. Note 0 is a REAL value the backend does send —
   * check `!== undefined`, never falsiness.
   */
  pointsBalance?: number;

  /**
   * All four avatar fields are always present, null/false included.
   * `showAnimatedBorder` is the holder's own setting — never derive it from
   * isElite; the client ANDs the two.
   */
  avatarUrl: string | null;
  showAnimatedBorder: boolean;
  borderColor1: string | null;
  borderColor2: string | null;

  availableLanguages?: Array<{ lang: string; scryfallId: string; setCode?: string }>;
}

export interface GetCardHoldersResponse {
  /** One entry per offerable COPY, best condition first. */
  holders: CardHolder[];
  /**
   * Distinct eligible holders — PEOPLE, not copies (COUNT(DISTINCT userId)).
   * Equals the `holderCount` badge on the same search row by construction.
   */
  totalHolders: number;
  /**
   * Distinct holders with at least one priceable copy — the offerable subset.
   * The gap against totalHolders is exactly the set nobody can act on, which is
   * why the header renders both: "N available to trade · M holding in total".
   */
  totalAvailableToTrade: number;
}

export interface GetCardHoldersArgs {
  /** Numeric MTGCard.id or a Scryfall UUID; the backend accepts either. */
  cardId: number | string;
  /** Clamped 1–100 server-side. */
  limit?: number;
}

/**
 * Traders holding a specific printing and able to trade it right now — the
 * counterparty picker behind "Make an offer" on a search result.
 *
 * `requiresAuth: false` is deliberate and load-bearing: baseQuery defaults that
 * flag to `true` when omitted, which force-logs-out on any 401. This endpoint is
 * public (a missing token never 401s), so a transient failure must not end the
 * session. The cookie still rides along, which is how the backend excludes the
 * viewer's own copies and anyone who has them blocked.
 *
 * `sort` is intentionally NOT sent. Sorting is client-side over the returned
 * page so toggling never refetches, and keeping it out of the args keeps it out
 * of the cache key. Were it ever sent, note the backend 400s on an unrecognised
 * value rather than falling back — unlike search-light, which fails open.
 */
export const cardHoldersApi = createApi({
  reducerPath: 'cardHoldersApi',
  baseQuery: baseQuery,
  tagTypes: ['CardHolders'],
  endpoints: (builder) => ({
    getCardHolders: builder.query<GetCardHoldersResponse, GetCardHoldersArgs>({
      query: ({ cardId, limit = 100 }) => ({
        url: `/cards/${cardId}/holders?limit=${limit}`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: ['CardHolders'],
    }),
  }),
});

export const { useGetCardHoldersQuery, useLazyGetCardHoldersQuery } = cardHoldersApi;
